import {
  useExecuteHardDelete,
  useHardDeleteImpact,
} from '@/hooks/queries/users';
import { User } from '@/types/user';
import {
  Button,
  Checkbox,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
  Textarea,
} from '@nextui-org/react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';

const HARD_DELETE_CONFIRMATION = 'HARD DELETE';

interface HardDeleteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
}

function getPendingStatusLabel(status: string): string {
  if (status === 'running') {
    return 'A hard-delete request is already running for this user.';
  }

  return 'A hard-delete request is already queued for this user.';
}

export default function HardDeleteUserModal({
  isOpen,
  onClose,
  user,
}: HardDeleteUserModalProps) {
  const [reason, setReason] = useState('');
  const [confirmationText, setConfirmationText] = useState('');
  const [acknowledgedOtherTenants, setAcknowledgedOtherTenants] =
    useState(false);

  const userId = user?.id ?? null;

  const {
    data: impact,
    isLoading: isLoadingImpact,
    isError: isImpactError,
    error: impactError,
  } = useHardDeleteImpact(userId, isOpen);

  const impactNotFound =
    isImpactError && (impactError as any)?.response?.status === 404;

  const pendingRequest = impact?.pending_hard_delete_request ?? null;

  const { mutate: executeHardDelete, isPending: isSubmitting } =
    useExecuteHardDelete({
      onSuccess: (response) => {
        toast.success(
          response.status === 'running'
            ? 'Hard delete is already in progress.'
            : 'Hard delete queued. The user has been deactivated.'
        );
        onClose();
      },
    });

  useEffect(() => {
    if (!isOpen) return;

    setReason('');
    setConfirmationText('');
    setAcknowledgedOtherTenants(false);
  }, [isOpen, userId]);

  const displayEmail = impact?.email || user?.email || 'this user';

  const requiresOtherTenantAck = Boolean(impact?.has_other_active_tenants);

  const canSubmit = useMemo(() => {
    if (!impact || pendingRequest || impactNotFound) return false;
    if (!reason.trim()) return false;
    if (confirmationText.trim() !== HARD_DELETE_CONFIRMATION) return false;
    if (requiresOtherTenantAck && !acknowledgedOtherTenants) return false;
    return true;
  }, [
    impact,
    pendingRequest,
    impactNotFound,
    reason,
    confirmationText,
    requiresOtherTenantAck,
    acknowledgedOtherTenants,
  ]);

  const handleSubmit = () => {
    if (!user || !canSubmit) return;

    executeHardDelete({
      userId: user.id,
      payload: {
        confirmation: HARD_DELETE_CONFIRMATION,
        reason: reason.trim(),
        acknowledged_other_tenants: requiresOtherTenantAck
          ? acknowledgedOtherTenants
          : false,
      },
    });
  };

  const renderBody = () => {
    if (isLoadingImpact) {
      return (
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      );
    }

    if (impactNotFound) {
      return (
        <div className="space-y-2 rounded-lg border border-warning-200 bg-warning-50 p-4 text-sm text-default-700">
          <p className="font-medium text-warning-800">
            User not found in the active tenant.
          </p>
        </div>
      );
    }

    if (isImpactError) {
      return (
        <div className="space-y-3">
          <p className="text-sm text-danger">
            Could not load hard-delete details. Please try again.
          </p>
        </div>
      );
    }

    if (pendingRequest) {
      return (
        <div className="space-y-2 rounded-lg border border-warning-200 bg-warning-50 p-4 text-sm text-default-700">
          <p className="font-medium text-warning-800">
            {getPendingStatusLabel(pendingRequest.status)}
          </p>
          {pendingRequest.request_id && (
            <p className="text-xs text-default-500">
              Request ID: {pendingRequest.request_id}
            </p>
          )}
        </div>
      );
    }

    if (!impact) return null;

    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-danger-200 bg-danger-50 p-4 text-sm text-default-700">
          <p className="font-medium text-danger">
            Hard delete is global and destructive
          </p>
          <p className="mt-2">
            This permanently deletes{' '}
            <span className="font-medium">{displayEmail}</span> and their data
            across the platform. The user is deactivated immediately.
          </p>
        </div>

        {impact.has_other_active_tenants && (
          <div className="space-y-3 rounded-lg border border-warning-200 bg-warning-50 p-4 text-sm text-default-700">
            <p className="font-medium text-warning-800">
              This user also belongs to other active tenants
            </p>
            <p>
              Hard deleting this user removes their data globally, not only from
              the current tenant.
            </p>
            {impact.active_tenants.length > 0 && (
              <ul className="list-disc pl-5">
                {impact.active_tenants.map((tenant) => (
                  <li key={tenant.id}>
                    {tenant.name} ({tenant.slug})
                  </li>
                ))}
              </ul>
            )}
            <Checkbox
              isSelected={acknowledgedOtherTenants}
              onValueChange={setAcknowledgedOtherTenants}
            >
              I understand this user belongs to other active tenants
            </Checkbox>
          </div>
        )}

        <Textarea
          label="Reason"
          placeholder="Explain why this hard delete is being performed"
          value={reason}
          onValueChange={setReason}
          minRows={3}
          isRequired
        />

        <Input
          label="Confirmation"
          placeholder={`Type ${HARD_DELETE_CONFIRMATION}`}
          value={confirmationText}
          onValueChange={setConfirmationText}
          description={`Type ${HARD_DELETE_CONFIRMATION} to confirm`}
          isRequired
        />
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalContent>
        <ModalHeader className="text-medium ">Hard delete user</ModalHeader>
        <ModalBody>{renderBody()}</ModalBody>
        <ModalFooter className="justify-between border-t mt-4">
          <Button
            variant="bordered"
            onPress={onClose}
            isDisabled={isSubmitting}
          >
            {pendingRequest || impactNotFound ? 'Close' : 'Cancel'}
          </Button>
          {!pendingRequest && !impactNotFound && impact && (
            <Button
              color="primary"
              onPress={handleSubmit}
              isLoading={isSubmitting}
              isDisabled={!canSubmit}
            >
              Hard delete user
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
